import { useEffect, useState } from 'react';

import { Commit } from 'src/components/types';
import urls from 'src/urls/commit';
import { publishUiEvent } from 'src/utils/analytics/publish';
import authRequest, { jsonHeaders } from 'src/utils/fetch';

export function useCommitWatchHooks(
  commit: Commit,
  currentUserKey: string
): {
  watchCommit: () => void;
  unwatchCommit: () => void;
  isWatching: boolean | undefined;
  isWatchLoading: boolean;
} {
  const { hash } = commit;
  const fullSlug = commit.repository.full_name;
  const url = urls.api.internal.watch(fullSlug, hash);

  const [isWatching, setWatching] = useState<boolean | undefined>(undefined);
  const [isWatchLoading, setWatchLoading] = useState(false);

  const watchCommit = async () => {
    setWatchLoading(true);

    try {
      publishUiEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'watchOrStopWatchingButton',
        source: 'commitPage',
      });
      const watchRequest = authRequest(url, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({
          uuid: currentUserKey,
        }),
      });

      const response = await fetch(watchRequest);
      if (!response.ok) {
        throw new Error('An error occured watching the commit');
      }

      setWatchLoading(false);
      setWatching(true);
      // response comes back empty so there is nothing to return
      return true;
    } catch (e) {
      setWatchLoading(false);
      return false;
    }
  };

  const unwatchCommit = async () => {
    setWatchLoading(true);

    try {
      publishUiEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'watchOrStopWatchingButton',
        source: 'commitPage',
      });
      const stopWatchingRequest = authRequest(url, {
        method: 'DELETE',
        headers: jsonHeaders,
      });

      const response = await fetch(stopWatchingRequest);
      if (!response.ok) {
        throw new Error('An error occured unwatching the commit');
      }

      setWatchLoading(false);
      setWatching(false);
      // response comes back empty so there is nothing to return
      return true;
    } catch (e) {
      setWatchLoading(false);
      return false;
    }
  };

  useEffect(() => {
    // If no user is logged in, short circuit this out so we don't 401
    // and pop up an auth prompt in the browser
    if (currentUserKey === '') {
      setWatchLoading(false);
      setWatching(false);
      return;
    }
    setWatchLoading(true);
    const getWatchingStatus = async () => {
      try {
        const response = await fetch(authRequest(url));
        if (!response.ok) {
          throw new Error('An error occured fetching commit watch status');
        }
        const { is_watching } = await response.json();
        setWatching(is_watching);
        return true;
      } catch (e) {
        return false;
      }
    };
    getWatchingStatus();
    setWatchLoading(false);
  }, [setWatchLoading, url, setWatching, currentUserKey]);

  return {
    watchCommit,
    unwatchCommit,
    isWatching,
    isWatchLoading,
  };
}
